import * as React from "react";
import {Card, CardActions, CardHeader, CardText, CardMedia, CardTitle} from "material-ui/Card";
import CardExpandable from "./CardExpandable";
import styles = require("./style.less");
import * as cn from "classnames";
import {FormSectionBase, FormSectionBaseProps} from "components/form/Sections/FormSectionBase";
import Expandable, {ExpandableProps, ExpandableContainerProps} from "components/Expandable/Expandable";
import {formSectionClass, formSectionTitleClass} from "uiTestClasses";

export interface SummaryNode {
    node: React.ReactNode;
    type: "placeholder" | "summary" | "default";
}

export enum CardFill {
    None,
    FillRight,
    FillAll
}

interface ExpandableFormSectionProps extends FormSectionBaseProps {
    summary: SummaryNode;
    title: React.ReactNode;
    help?: React.ReactNode;
    focusOnExpandAll?: boolean;
    fillCardWidth?: CardFill;
}

type Props = ExpandableFormSectionProps & ExpandableProps & ExpandableContainerProps;

export class ExpandableFormSectionInternal extends FormSectionBase<Props> {
    focusControl = false;
    ignore = false;

    constructor(props: Props) {
        super(props);
        this.ignore = this.props.isExpandedByDefault; //don't focus the first time we expand due to being expandedByDefault
    }

    // we can't just use actAsExpander on card Title because it eats the click
    // and we can't add links to the title in help text or our show more info link
    handleExpand = (e: any) => {
        if (e.target.tagName.toLowerCase() !== "a") {
            e.preventDefault();
            this.props.onExpandedChanged(!this.props.isExpanded);
        }
    }

    componentWillReceiveProps(nextProps: Props) {
        if (this.props.isExpanded === false && nextProps.isExpanded === true) {
            if (this.ignore) {
                this.ignore = false;
            } else {
                this.focusControl = true;
            }
        }
    }

    render() {
        const summary = this.props.summary.type === "placeholder"
            ?  <span className={styles.cardPlaceholderSubTitle}>{this.props.summary.node}</span>
            : this.props.summary.type === "default"
                ? <span className={styles.cardDefaultSubTitle}>{this.props.summary.node}</span>
                : <span className={styles.cardSubTitle}>{this.props.summary.node}</span>;

        const subtitle = this.props.isExpanded ? this.help(this.props.help) : summary;
        const title = this.title(this.props.title, subtitle);
        const bodyclasses = cn({
            [styles.cardMedia]: true,
            [styles.cardMediaFillRight]: this.props.fillCardWidth === CardFill.FillRight || this.props.fillCardWidth === CardFill.FillAll,
            [styles.cardMediaFillAll]: this.props.fillCardWidth === CardFill.FillAll
        });

        return <Card
            className={`${formSectionClass} ${styles.formExpander}`}
            onExpandChange={this.props.onExpandedChanged}
            expanded={this.props.isExpanded}
            >
            <CardTitle
                title={title}
                onClick={this.handleExpand}
                showExpandableButton={false}
            />
            {this.body(bodyclasses, true)}
            {this.moreInformation()}
        </Card>;
    }

    protected title(title: React.ReactNode, subtitle: React.ReactNode) {
        return <div className={styles.cardTitleContainer}>
            <span className={`${formSectionTitleClass} ${styles.cardTitle}`}>{title}</span>
            {subtitle}
            <div className={styles.expandCollapse}>
                <CardExpandable tabIndex={this.props.isExpanded ? -1 : 0}
                                expanded={this.props.isExpanded} />
            </div>
        </div>;
    }

    protected alterChild = (child: any): any => {
        const focusOnExpand = this.props.expandingAll === false || this.props.focusOnExpandAll;
        if (focusOnExpand && this.focusControl && this.isFocusableControl(child)) {
            this.focusControl = false;
            return React.cloneElement(child, {
                ...child.props,
                autoFocus: true
            });
        } else {
            return child;
        }
    }

    private isFocusableControl = (child: any) => {
        return child.props && (child.props.onChange || child.props.setProperties) && !child.props.doNotAutoFocus;
    }
}

const ExpandableFormSection = Expandable<ExpandableFormSectionProps>(ExpandableFormSectionInternal);
export {ExpandableFormSectionProps};
export default ExpandableFormSection;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/form/Sections/ExpandableFormSection.tsx